import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { inr, fmtDate, today } from "@/lib/format";
import { toast } from "sonner";

const STATUSES = ["Pending", "In Transit", "Delivered", "Cancelled"];
const EMPTY = {
  origin: "",
  destination: "",
  goods: "",
  weight_tons: 0,
  freight_amount: 0,
  status: "Pending",
  customer_id: "",
  customer_name: "",
  vehicle_id: "",
  vehicle_no: "",
  driver_id: "",
  driver_name: "",
  dispatch_date: today(),
  delivery_date: "",
};

export default function Shipments() {
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [s, c, v, d] = await Promise.all([
        api.get("/shipments"),
        api.get("/customers"),
        api.get("/vehicles"),
        api.get("/drivers"),
      ]);
      setRows(s.data);
      setCustomers(c.data);
      setVehicles(v.data);
      setDrivers(d.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, dispatch_date: today() });
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...EMPTY, ...row });
    setOpen(true);
  };

  const save = async () => {
    if (!form.origin || !form.destination) {
      toast.error("Origin and destination are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/shipments/${editing.id}`, form);
      else await api.post("/shipments", form);
      toast.success(editing ? "Shipment updated" : "Shipment created");
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this shipment?")) return;
    try {
      await api.delete(`/shipments/${row.id}`);
      toast.success("Shipment deleted");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Shipments"
        subtitle="Create and track consignments across your fleet."
        action={
          <Button data-testid="add-shipment-btn" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> New Shipment
          </Button>
        }
      />

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {["Consignment", "Route", "Customer", "Vehicle", "Freight", "Dispatch", "Status"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  {h}
                </TableHead>
              ))}
              <TableHead className="w-[90px] text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-12 text-center text-muted-foreground">
                  No shipments yet. Create your first consignment.
                </TableCell>
              </TableRow>
            )}
            {rows &&
              rows.map((r) => (
                <TableRow key={r.id} data-testid="shipments-row">
                  <TableCell className="font-medium">{r.consignment_no}</TableCell>
                  <TableCell className="text-sm">
                    {r.origin} <span className="text-muted-foreground">→</span> {r.destination}
                  </TableCell>
                  <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm">{r.vehicle_no || "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.freight_amount)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.dispatch_date)}</TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid="edit-shipment-btn">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => remove(r)}
                        data-testid="delete-shipment-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[620px]">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Shipment" : "New Shipment"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Origin</Label>
              <Input data-testid="field-origin" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} placeholder="Mumbai" />
            </div>
            <div className="grid gap-1.5">
              <Label>Destination</Label>
              <Input data-testid="field-destination" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="Delhi" />
            </div>
            <div className="grid gap-1.5">
              <Label>Customer</Label>
              <Select
                value={form.customer_id}
                onValueChange={(v) => {
                  const c = customers.find((x) => x.id === v);
                  setForm({ ...form, customer_id: v, customer_name: c ? c.name : "" });
                }}
              >
                <SelectTrigger data-testid="field-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="field-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Vehicle</Label>
              <Select
                value={form.vehicle_id}
                onValueChange={(v) => {
                  const veh = vehicles.find((x) => x.id === v);
                  setForm({ ...form, vehicle_id: v, vehicle_no: veh ? veh.registration_no : "" });
                }}
              >
                <SelectTrigger data-testid="field-vehicle"><SelectValue placeholder="Assign vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.registration_no}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Driver</Label>
              <Select
                value={form.driver_id}
                onValueChange={(v) => {
                  const d = drivers.find((x) => x.id === v);
                  setForm({ ...form, driver_id: v, driver_name: d ? d.name : "" });
                }}
              >
                <SelectTrigger data-testid="field-driver"><SelectValue placeholder="Assign driver" /></SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Goods</Label>
              <Input data-testid="field-goods" value={form.goods} onChange={(e) => setForm({ ...form, goods: e.target.value })} placeholder="Steel coils" />
            </div>
            <div className="grid gap-1.5">
              <Label>Weight (tons)</Label>
              <Input type="number" data-testid="field-weight" value={form.weight_tons} onChange={(e) => setForm({ ...form, weight_tons: Number(e.target.value) })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Freight amount (₹)</Label>
              <Input type="number" data-testid="field-freight" value={form.freight_amount} onChange={(e) => setForm({ ...form, freight_amount: Number(e.target.value) })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Dispatch date</Label>
              <Input type="date" data-testid="field-dispatch" value={form.dispatch_date} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Delivery date</Label>
              <Input type="date" data-testid="field-delivery" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-shipment-btn">
              {saving ? "Saving…" : "Save Shipment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
